import {
  Box,
  Collapse,
  ListItemButton,
  ListItemIcon,
  Typography,
} from "@mui/material";
import { TriangleIcon } from "../../../images";
import { CustomCheckbox } from "../../CustomCheckbox";
import { useState } from "react";
import { SectionItemType } from "../../../../types/apiTypes";

const SubListItem = ({
  topicKey,
  sectionKey,
  subTopicLabel,
  values,
  selectSubSectionHandler,
  selectArticleSectionHandler,
  isChecked,
}: {
  topicKey: string;
  sectionKey: string;
  subTopicLabel: string;
  values: Record<string, SectionItemType> | {};
  selectSubSectionHandler: (section: string, subSection: string) => void;
  selectArticleSectionHandler: (
    section: string,
    subSection: string,
    articleKey: string,
  ) => void;
  isChecked: boolean;
}) => {
  const isLeaf = Object.keys(values).length === 0;
  const hasChildren = !isLeaf;
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const valuesEntries = Object.entries(values);

  return (
    <Box>
      <ListItemButton
        sx={{ paddingLeft: "46px", flexDirection: "row", gap: "4px" }}
        onClick={(e) => {
          if (hasChildren) {
            setIsOpen((s) => !s);
          } else {
            e.stopPropagation();
            selectSubSectionHandler(topicKey, sectionKey);
          }
        }}
      >
        <ListItemIcon
          sx={{ minWidth: "10px" }}
          onClick={(e) => {
            if (valuesEntries.length > 0) {
              e.stopPropagation();
              selectSubSectionHandler(topicKey, sectionKey);
            }
          }}
        >
          {hasChildren &&
            (isOpen ? <TriangleIcon /> : <TriangleIcon rotate="-90" />)}
        </ListItemIcon>

        <CustomCheckbox
          checked={isChecked}
          onClick={(e) => {
            e.stopPropagation();
            selectSubSectionHandler(topicKey, sectionKey);
          }}
          indeterminate={
            !isChecked && valuesEntries.some(([_, item]) => item.checked)
          }
        />

        <Typography>{subTopicLabel}</Typography>
      </ListItemButton>

      {hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {valuesEntries.map(([articleKey, item]) => (
            <ListItemButton
              key={articleKey}
              sx={{ paddingLeft: "60px" }}
              onClick={() =>
                selectArticleSectionHandler(topicKey, sectionKey, articleKey)
              }
            >
              <CustomCheckbox
                checked={item.checked}
                onClick={(e) => {
                  e.stopPropagation();
                  selectArticleSectionHandler(topicKey, sectionKey, articleKey);
                }}
              />

              <Typography>{item.label}</Typography>
            </ListItemButton>
          ))}
        </Collapse>
      )}
    </Box>
  );
};

export const ListItem = ({
  label,
  sectionKey,
  values,
  selectSectionHandler,
  selectSubSectionHandler,
  selectArticleSectionHandler,
  isChecked,
}: {
  label: string;
  sectionKey: string;
  values: Record<string, SectionItemType> | {};
  selectSectionHandler: (section: string) => void;
  selectSubSectionHandler: (section: string, subSection: string) => void;
  selectArticleSectionHandler: (
    section: string,
    subSection: string,
    articleKey: string,
  ) => void;
  isChecked: boolean;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  if (!values) return null;

  const isLeaf = Object.keys(values).length === 0;
  const hasChildren = !isLeaf;
  const valuesEntries = Object.entries(values);

  return (
    <Box>
      <ListItemButton
        sx={{ paddingLeft: "16px", flexDirection: "row", gap: "4px" }}
        onClick={(e) => {
          hasChildren && setIsOpen((s) => !s);
          if (valuesEntries.length === 0) {
            e.stopPropagation();
            selectSectionHandler(sectionKey);
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: "10px" }}>
          {hasChildren &&
            (isOpen ? <TriangleIcon /> : <TriangleIcon rotate="-90" />)}
        </ListItemIcon>
        <CustomCheckbox
          checked={isChecked}
          onClick={(e) => {
            e.stopPropagation();
            selectSectionHandler(sectionKey);
          }}
          indeterminate={
            !isChecked && valuesEntries.some(([_, item]) => item.checked)
          }
        />

        <Typography>{label}</Typography>
      </ListItemButton>

      {hasChildren && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {valuesEntries.map(([subKey, item]) => (
            <SubListItem
              key={subKey}
              sectionKey={subKey}
              topicKey={sectionKey}
              subTopicLabel={item.label}
              values={item.value}
              selectSubSectionHandler={selectSubSectionHandler}
              selectArticleSectionHandler={selectArticleSectionHandler}
              isChecked={item.checked}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};
